import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  ArrowRight,
  Phone,
  Award,
  MapPin,
  Shield,
  Check,
  Star,
  MessageSquare,
  TrendingUp,
  BadgeDollarSign,
} from "lucide-react";
import {
  companyInfo,
  whyChooseReasons,
  faqItems,
  testimonials,
} from "../data/mock";
import { useScrollReveal } from "../hooks/useScrollReveal";

const iconMap = { Award, MapPin, MessageSquare, TrendingUp, BadgeDollarSign, Shield };

const Section = ({ children, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      ref={ref}
      className={`reveal ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </section>
  );
};

export default function WhyChoose() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="container mx-auto px-6 page-enter">
          <div className="max-w-3xl">
            <Badge
              variant="secondary"
              className="mb-4 px-3 py-1 text-blue-700 bg-blue-50 border border-blue-100 text-xs font-medium uppercase tracking-wider"
            >
              Why FlexiTech
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4">
              Why Australian Businesses Choose FlexiTech
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              We're not just another IT provider. Here's why over 500 businesses
              trust us with their technology.
            </p>
          </div>
        </div>
      </section>

      {/* Reasons */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseReasons.map((reason, i) => {
              const Icon = iconMap[reason.icon] || Award;
              return (
                <div key={i} className="group">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-5 group-hover:bg-blue-100 transition-colors duration-300">
                    <Icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    {reason.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* Comparison */}
      <Section className="py-16 lg:py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-4 text-center">
              FlexiTech vs. Large MSPs
            </h2>
            <p className="text-lg text-slate-600 text-center mb-10">
              See how we compare to the big-name providers.
            </p>

            <Card className="border-slate-200 overflow-hidden">
              <CardContent className="p-0">
                <div className="grid grid-cols-3 bg-slate-100 px-6 py-3 text-sm font-semibold text-slate-700">
                  <div>Feature</div>
                  <div className="text-center text-blue-600">FlexiTech</div>
                  <div className="text-center text-slate-500">Large MSPs</div>
                </div>
                {[
                  ["Dedicated Account Manager", true, false],
                  ["Fixed Monthly Pricing", true, false],
                  ["< 15min Response Time", true, false],
                  ["No Lock-in Contracts", true, false],
                  ["Local Australian Team", true, true],
                  ["Industry Specialisation", true, false],
                  ["24/7 Emergency Support", true, true],
                  ["Personalised Service", true, false],
                ].map(([feature, us, them], i) => (
                  <div
                    key={i}
                    className={`grid grid-cols-3 px-6 py-3.5 text-sm ${
                      i % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                    } border-t border-slate-100`}
                  >
                    <div className="text-slate-700">{feature}</div>
                    <div className="text-center">
                      {us ? (
                        <Check className="w-5 h-5 text-blue-600 mx-auto" />
                      ) : (
                        <span className="text-slate-300">{"\u2014"}</span>
                      )}
                    </div>
                    <div className="text-center">
                      {them ? (
                        <Check className="w-5 h-5 text-slate-400 mx-auto" />
                      ) : (
                        <span className="text-slate-300">{"\u2014"}</span>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </Section>

      {/* Testimonials */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-10 text-center">
            What Our Clients Say
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {testimonials.slice(0, 2).map((t, i) => (
              <Card key={i} className="testimonial-card border-slate-100">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star
                        key={j}
                        className="w-4 h-4 fill-amber-400 text-amber-400"
                      />
                    ))}
                  </div>
                  <blockquote className="text-slate-700 leading-relaxed mb-5">
                    "{t.quote}"
                  </blockquote>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-sm font-semibold text-blue-700">
                      {t.name.split(" ").map(n => n[0]).join("")}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">
                        {t.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {t.role}, {t.company}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-16 lg:py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-4 text-center">
              Common Questions
            </h2>
            <p className="text-lg text-slate-600 text-center mb-10">
              Have questions? Here are the most common ones we hear.
            </p>
            <Accordion type="single" collapsible className="space-y-3">
              {faqItems.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border border-slate-200/80 rounded-xl px-6 bg-white data-[state=open]:border-blue-100 data-[state=open]:bg-blue-50/30 transition-colors"
                >
                  <AccordionTrigger className="text-left text-slate-900 font-medium hover:no-underline py-5">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed pb-5 text-sm">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-slate-900 relative grain-overlay">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white tracking-tight mb-4">
              Experience the FlexiTech Difference
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Join 500+ Australian businesses who've made the switch to better
              IT support.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white btn-primary h-12 px-8"
              >
                <Link to="/contact">
                  Get Started Today
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 px-8 border-slate-600 text-white hover:bg-slate-800 hover:text-white"
              >
                <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
